<template>
  <wui-icon
    v-if="event.status === MessageStatusType.INITIAL"
    name="spinner"
    class="shrink-0 select-none text-fg-base-fg22"
    small
    :title="$t('common.sending')"
  />
  <div
    v-else-if="event.status === MessageStatusType.ERROR"
    class="group relative flex shrink-0 cursor-pointer"
  >
    <wui-icon
      data-test="message-status-error-icon"
      name="warningM"
      class="shrink-0 text-surf-neg-base-def"
      small
    />
    <div
      data-test="message-status-error-hint"
      class="hidden absolute bg-elev-context-menu-bg text-fg-base-fg3 text-1316 right-8 -top-2 shadow-overlay rounded-4 px-4 py-2 text-nowrap"
      :class="{
        'group-hover:block' : showError
      }"
    >
      {{ $t('common.unsent') }}
    </div>
  </div>
  <template v-else-if="!isGroup">
    <div
      v-if="event.eventId && lastReadEventId >= event.eventId"
      :title="`${$t('common.read')} ${readTime}`"
      data-test="message-status-read"
      class="h-4! flex shrink-0 items-center"
    >
      <wui-icon
        small
        class="-mr-[12px] shrink-0"
        :class="{
          'text-surf-brand-basedarkblue-def': state === 'default' || state === 'item-selected',
          'text-primary-tints-600': state === 'call-session',
        }"
        name="check"
      />
      <wui-icon
        small
        class="shrink-0"
        :class="{
          'text-surf-brand-basedarkblue-def': state === 'default' || state === 'item-selected',
          'text-primary-tints-600': state === 'call-session',
        }"
        name="check"
      />
    </div>
    <div
      v-else
      :title="`${$t('chat.delivered')} ${readTime}`"
      data-test="message-status-delivered"
      class="flex shrink-0 items-center"
    >
      <wui-icon
        small
        class="h-4! w-4! shrink-0"
        :class="{
          'text-black-500': state === 'default',
          'text-black-600': state === 'item-selected',
          'text-primary-tints-250': state === 'call-session',
        }"
        name="check"
      />
    </div>
  </template>
  <template v-else>
    <div
      v-if="hasGroupReaders"
      :title="$t('common.read')"
      data-test="message-status-read"
      class="h-4! flex shrink-0 items-center"
    >
      <wui-icon
        small
        class="-mr-[12px] shrink-0"
        :class="{
          'text-surf-brand-basedarkblue-def': state === 'default' || state === 'item-selected',
          'text-primary-tints-600': state === 'call-session',
        }"
        name="check"
      />
      <wui-icon
        small
        class="shrink-0"
        :class="{
          'text-surf-brand-basedarkblue-def': state === 'default' || state === 'item-selected',
          'text-primary-tints-600': state === 'call-session',
        }"
        name="check"
      />
    </div>
    <div
      v-else
      :title="`${$t('chat.delivered')} ${readTime}`"
      data-test="message-status-delivered"
      class="flex shrink-0 items-center"
    >
      <wui-icon
        small
        class="h-4! w-4! shrink-0"
        :class="{
          'text-black-500': state === 'default',
          'text-black-600': state === 'item-selected',
          'text-primary-tints-250': state === 'call-session',
        }"
        name="check"
      />
    </div>
  </template>
</template>

<script setup lang="ts">
import { WuiIcon } from '@wui/common-library'
import { HistoryMessageItem, MessageOutgoingEvent, MessageIncomingEvent, MessageStatusType } from '@wui/im'
import { unix } from 'dayjs'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useStoreLastReadEventIds } from '@/entities/room'

import { UI_DATE_TIME_FORMAT } from '@/shared/utils/constants'

const props = withDefaults(defineProps<{
  contactId: string,
  event: HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>,
  isGroup: boolean,
  state?: 'default' | 'item-selected' | 'call-session'
  showError?: boolean
}>(),
{
  state: 'default',
  showError: false,
})

const lastReadStore = useStoreLastReadEventIds()
const { lastMessageEventIdByRoomAndContactId } = storeToRefs(lastReadStore)
const {
  getMessageEventIdByRoomAndContactId,
  getTimestampsByMessageEventId,
  getViewersByMessageEventId,
} = lastReadStore

const lastReadEventId = computed(() => {
  return getMessageEventIdByRoomAndContactId(props.contactId, props.contactId)
})

const hasGroupReaders = computed(() => {
  if (!props.isGroup || !props.event.eventId) return false
  void lastMessageEventIdByRoomAndContactId.value[props.contactId]
  return (getViewersByMessageEventId(props.contactId, props.event.eventId) ?? []).length > 0
})

const readTime = computed(() => {
  if (!props.event.eventId) {
    return ''
  }

  const timestamp = getTimestampsByMessageEventId(props.event.eventId)?.find(item => item.contactId === props.contactId)

  if (timestamp) {
    return unix(timestamp.timestamp).tz().format(UI_DATE_TIME_FORMAT)
  }

  return ''
})
</script>
